import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { RouterService } from '@core/services/router.service';
import { NextStep, UpNextService } from '@core/services/up-next.service';
import { ProductModel } from '@core/store/entities/product/product.model';
import { SortedTasks } from '@core/store/entities/task/task.model';
import { ComponentChanges } from '@shared/utils/general.utils';
import { Nullable } from '@shared/utils/type.utils';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LayoutService } from '@core/services/layout.service';
import { ProductType } from '@core/models/api/dsm-types';
import { UwIssuesByProduct } from '@entities/uw-issues/uw-issues.selector';
import { UserModel } from '@entities/user/user.model';
import { UserService } from '@core/services/user.service';
import { EvtAlertStoreEntity } from '@entities/evt-alert/evt-alert.reducer';
import { EvtAgentReply } from '@entities/evt-alert/evt-alert.adapter';
import { FeatureFlagsModel } from '@entities/feature-flag/feature-flag.model';
import { FeatureFlagService } from '@core/services/feature-flag.service';

@Component({
    selector: 'nwx-next-step',
    templateUrl: './next-step.component.html',
    styleUrls: ['./next-step.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NextStepComponent implements OnChanges, OnDestroy {
  @Input() products!: ProductModel[];
  @Input() uwIssues: UwIssuesByProduct = {};
  @Input() disabledUwProducts: ProductType[] = [];
  @Input() errorTasks!: Nullable<SortedTasks[][]>;
  @Input() evtAlerts: EvtAlertStoreEntity[] = [];
  @Input() savedAgentReply!: Nullable<EvtAgentReply>;
  @Input() blockedTasks!: Nullable<SortedTasks[][]>;
  @Input() tasks!: Nullable<SortedTasks[][]>;
  @Input() reviewTasks!: Nullable<SortedTasks[][]>;
  @Input() showTasks!: boolean;
  @Input() showIncompleteError = false;

  @Output() taskButtonClicked: EventEmitter<string> = new EventEmitter();
  @Output() clickEvent = new EventEmitter();

  nextStep$: Observable<NextStep>;
  maxSizeMedium$: Observable<boolean>;
  user$: Observable<UserModel>;
  featureFlags$: Observable<FeatureFlagsModel>;
  unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private changeDetector: ChangeDetectorRef,
    private routerService: RouterService,
    private upNextService: UpNextService,
    private layoutService: LayoutService,
    private userService: UserService,
    private featureFlagService: FeatureFlagService
  ) {
    this.nextStep$ = this.upNextService.getNextStep().pipe(
      tap((nextStep) => {
        if (nextStep.buttonType === 'recalculate') {
          this.showTasks = false;
        }
      })
    );
    this.maxSizeMedium$ = this.layoutService.maxSizeMedium();
    this.user$ = this.userService.getUser();
    this.featureFlags$ = this.featureFlagService.getAllFeatureFlags();
  }

  ngOnChanges(changes: ComponentChanges<NextStepComponent>): void {
    if (
      changes.showIncompleteError &&
      changes.showIncompleteError.currentValue === true
    ) {
    } else {
      this.showIncompleteError = false;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  navigateToPage(url: string): void {
    this.routerService.go({ path: [url || '#'] });
  }

  getStatusMessageForProduct(product: ProductModel): string {
    return this.upNextService.getStatusMessageForProduct(product);
  }

  onTaskButtonClicked(taskPageUrl: string): void {
    this.taskButtonClicked.emit(taskPageUrl);
  }

  detectChanges(): void {
    this.changeDetector.detectChanges();
  }
}
